import { useQSet } from '@exam-management/common/qset-provider';
import { getDifficultyType } from '@exam-management/components';
import {
  OrderType,
  QuestionGroupType,
  QuestionStatus,
  TagCategory,
  useGetUsedQSetQuestionsByQuestionIdsQuery,
  useSearchQuestionsToChangeQuery,
} from '@exam-management/graphql';
import { Link } from '@exam-management/router';
import { Box, Button, OpenInNewRoundedIcon } from '@shared/components';
import { useState } from 'react';

import {
  type FormSingleQuestionChangeTableProps,
  FormSingleQuestionChangeTable,
  IncompleteTopicAndSubTopicOverlay,
  QuestionNotFoundOverlay,
} from '../../components';
import { useQuestionChangeInformation } from '../../providers';

const QUESTION_CHANGE_TABLE_PAGE_SIZE = 10;

export default function SingleQuestionChangeTableSection() {
  const { qSet } = useQSet();
  const { id: qSetId } = qSet;
  const { subTopicId, isInformationLoading, qBankStructureId } = useQuestionChangeInformation();
  const [page, setPage] = useState(0);

  const { data: questionsToChangeResult, loading: isLoadingQuestionsToChange } =
    useSearchQuestionsToChangeQuery({
      variables: {
        search: {
          status: [QuestionStatus.Published],
          levelItemId: subTopicId,
          questionGroupType: [QuestionGroupType.Single, QuestionGroupType.LotOfQuestion],
          refQBankStructureId: qBankStructureId,
        },
        paging: {
          pageSize: QUESTION_CHANGE_TABLE_PAGE_SIZE,
          currentPage: page,
        },
        order: {
          field: 'refCode',
          type: OrderType.Desc,
        },
      },
      fetchPolicy: 'no-cache',
      skip: !subTopicId,
    });

  const questionsToChange = questionsToChangeResult?.qctSearchQuestions.questions ?? [];
  const totalQuestionsToChange = questionsToChangeResult?.qctSearchQuestions.total ?? 0;

  const { data: usedQSetQuestionsResult, loading: isLoadingUsedQSetQuestions } =
    useGetUsedQSetQuestionsByQuestionIdsQuery({
      variables: {
        input: {
          qSetId: qSetId,
          questionIds: questionsToChange.map((question) => question.id),
        },
      },
      fetchPolicy: 'no-cache',
      skip: !questionsToChange.length,
    });

  const usedQSetQuestions =
    usedQSetQuestionsResult?.excGetQSetQuestionsByQuestionIds.qSetQuestions ?? [];

  const rows: FormSingleQuestionChangeTableProps['rows'] = questionsToChange.map((question) => {
    const difficulty = getDifficultyType(
      question.tags.find((tag) => tag.category === TagCategory.Difficulty)?.name ?? '',
    );
    const calculated =
      question.tags.find((tag) => tag.category === TagCategory.Calculation)?.name ?? 'คำนวณ';
    const usedQSetQuestion = usedQSetQuestions.find(
      (usedQuestion) => usedQuestion.questionId === question.id,
    );

    return {
      id: question.id,
      qId: question.refCode,
      difficulty: difficulty,
      calculated: calculated === 'คำนวณ',
      questionStatus: usedQSetQuestion
        ? { used: true, questionNo: usedQSetQuestion.order }
        : { used: false },
      action: (
        <Link
          to='/qbank-structures/:structureId/qcontents/single/:contentId'
          params={{
            structureId: question.refQBankStructureId,
            contentId: question.id,
          }}
          target='_blank'
          rel='noreferrer'
        >
          <Button
            variant='outlined'
            color='secondary'
            size='small'
            startIcon={<OpenInNewRoundedIcon />}
          >
            ดูคำถาม
          </Button>
        </Link>
      ),
    };
  });

  const isLoading = isLoadingQuestionsToChange || isLoadingUsedQSetQuestions;

  if (isInformationLoading) {
    return null;
  }

  return (
    // This Box is using for prevent table from being scrollable, since it's need overflow property to hide an overflow border
    <Box>
      <FormSingleQuestionChangeTable
        name='questionId'
        rows={!isLoading ? rows : []}
        total={!isLoading ? totalQuestionsToChange : 0}
        loading={isLoading}
        pagination={{
          rowsPerPage: QUESTION_CHANGE_TABLE_PAGE_SIZE,
          onPageChange: (_, page) => {
            setPage(page);
          },
        }}
        slots={{
          noRowsOverlay: subTopicId ? (
            <QuestionNotFoundOverlay />
          ) : (
            <IncompleteTopicAndSubTopicOverlay />
          ),
        }}
      />
    </Box>
  );
}
